import { useMemo, useCallback } from 'react';
import { useSessionState } from './useSessionState';
import { useGetCurrentUser } from '@/hooks/api/users/useGetCurrentUser';
import { TenantUserSettings } from '@/api/generatedApi';

type OpenTabsState = Record<string, string[]>;

const SESSION_STORAGE_KEY = 'openSearchTabs';

export const useOpenSearchTabs = () => {
  const { currentTenant, tenants } = useGetCurrentUser();
  const currentTenantId = currentTenant?.tenantId;

  const [openTabsState, setOpenTabsState] = useSessionState<OpenTabsState>(SESSION_STORAGE_KEY, {});

  const openTabIds = useMemo(() => {
    if (!currentTenantId) return [];
    return openTabsState[currentTenantId] ?? [];
  }, [currentTenantId, openTabsState]);

  const openTabs = useMemo(() => {
    return openTabIds
      .map(tabId => tenants.find(t => t?.tenantId === tabId))
      .filter((t): t is TenantUserSettings => t !== undefined);
  }, [openTabIds, tenants]);

  const availableTenants = useMemo(() => {
    return tenants.filter(t => t && !openTabIds.includes(t.tenantId ?? ''));
  }, [tenants, openTabIds]);

  const addTab = useCallback(
    (tenant: TenantUserSettings) => {
      if (!currentTenantId || !tenant?.tenantId) return;

      setOpenTabsState(previous => {
        const currentTabs = previous[currentTenantId] ?? [];

        if (currentTabs.includes(tenant.tenantId ?? '')) {
          return previous;
        }

        if (currentTabs.length === 0) {
          const newTabs =
            tenant.tenantId === currentTenantId ? [currentTenantId] : [currentTenantId, tenant.tenantId ?? ''];
          return {
            ...previous,
            [currentTenantId]: newTabs,
          };
        }

        return {
          ...previous,
          [currentTenantId]: [...currentTabs, tenant.tenantId ?? ''],
        };
      });
    },
    [currentTenantId, setOpenTabsState]
  );

  const removeTab = useCallback(
    (tenantId: string) => {
      if (!currentTenantId) return;

      setOpenTabsState(previous => {
        const currentTabs = previous[currentTenantId] ?? [];
        const newTabs = currentTabs.filter(id => id !== tenantId);

        if (newTabs.length <= 1) {
          const result = { ...previous };
          delete result[currentTenantId];
          return result;
        }

        return {
          ...previous,
          [currentTenantId]: newTabs,
        };
      });
    },
    [currentTenantId, setOpenTabsState]
  );

  const hasOpenTabs = openTabs.length > 0;

  return {
    openTabs,
    availableTenants,
    addTab,
    removeTab,
    hasOpenTabs,
  };
};
